import React from 'react';
import { Link } from 'react-router-dom';
import TeamCrest from './TeamCrest';
import { formatDate, format12HourTime } from '../utils/formatters';
import '../styles/Matches.css';

const scoreOf = (team) => (team?.results || []).reduce((sum, r) => sum + (r.result || 0), 0);

/*
 * A match reads as a stacked scoreboard: one row per side, crest and name on
 * the left, score on the right. Rocket League is played blue side vs orange
 * side and the feed's `designation` says which is which, so that mapping — not
 * an arbitrary accent — drives the colour of each row.
 */
const MatchItem = ({ match, isCompleted = false, date, hideDate = false }) => {
  const teams = match.teams || [];
  const team1 = teams.find((t) => t.designation === 1) || teams[0];
  const team2 = teams.find((t) => t.designation === 2) || teams[1];

  const name1 = match.team_1_name || team1?.name || 'TBD';
  const name2 = match.team_2_name || team2?.name || 'TBD';

  const score1 = scoreOf(team1);
  const score2 = scoreOf(team2);

  const to = date ? `/match/${match.matchid}/${date}` : `/match/${match.matchid}`;
  const bestOf = match.match_format?.count;
  const showKickoff = !isCompleted && Boolean(match.time);

  // Lists group cards under a date separator, so repeating the date on the card
  // is pure duplication of the line directly above it.
  const timeText = match.time ? format12HourTime(match.time) : '';
  const whenText = hideDate
    ? (isCompleted ? timeText : '')
    : [formatDate(match.date), isCompleted ? timeText : ''].filter(Boolean).join(' · ');

  // Only a decided result changes emphasis — an upcoming match has no loser.
  const outcome = (mine, theirs) => {
    if (!isCompleted || mine === theirs) return '';
    return mine > theirs ? ' side-won' : ' side-lost';
  };

  const side = (name, score, variant, modifier) => (
    <div className={`side side-${variant}${modifier}`}>
      <TeamCrest name={name} size="sm" />
      <span className="side-name">{name}</span>
      {isCompleted && <span className="side-score tabular">{score}</span>}
    </div>
  );

  return (
    <Link to={to} className="match-card">
      {/* has-kickoff reserves room on the right so long team names ellipsis
          before the time rather than sliding underneath it. */}
      <div className={`match-sides${showKickoff ? ' has-kickoff' : ''}`}>
        {side(name1, score1, 'blue', outcome(score1, score2))}
        {side(name2, score2, 'orange', outcome(score2, score1))}

        {/* Completed cards are anchored on the right by the score; upcoming
            ones need the kickoff time there or they trail into empty space. */}
        {showKickoff && (
          <span className="match-kickoff tabular">{format12HourTime(match.time)}</span>
        )}
      </div>

      <div className="match-meta">
        {whenText && <span className="match-when tabular">{whenText}</span>}
        {match.league && <span className="match-league">{match.league}</span>}
        {bestOf ? <span className="match-format">BO{bestOf}</span> : null}
      </div>
    </Link>
  );
};

export default MatchItem;
